import React, { useState, useMemo, useCallback } from 'react';
import { Formik, FormikProps } from 'formik';

import { SelectControlOption } from 'modules/shared/types';
import {
    RadioInputControl,
    DatePickerControl,
    SelectControl,
    PasswordInputControl,
    EmailInputControl,
    InputControl,
} from 'modules/shared-form/components';
import { CMSImageContainer } from 'modules/shared-cms/components';

import { IdentificationFormProps, ListIdentification } from './identification-form-type';

import enhancer from './identification-form-enhancer';

import styles from './identification-form-component.module.css';
import { DenominatorTypeValues } from 'modules/shared/models';

const buildCodeErrorStr = (code: string, eventTypeId: string, storeId: string): string => {
    const missingFields = [];
    if (!code) {
        missingFields.push('Code avantage');
    }
    if (!eventTypeId) {
        missingFields.push("Type d'événement");
    }
    if (!storeId) {
        missingFields.push('Magasin');
    }
    return `Champs requis pour la vérification: ${missingFields.join(', ')}`;
};

const InnerIdentificationForm: React.FC<IdentificationFormProps> = ({
    picture,
    model,
    eventTypes,
    hasOrganiser,
    stores,
    offer,
    onSuccess,
    onIncentiveExist,
}: IdentificationFormProps) => {
    const [incentiveState, setIncentiveState] = useState({ isValid: false, error: '' });

    const loadingIncentives = false;

    const validateIncentive = useCallback(
        (props: FormikProps<ListIdentification>) => {
            if (!loadingIncentives) {
                const { code, eventTypeId, storeId } = props.values;
                if (code && eventTypeId && storeId) {
                    onIncentiveExist(code, eventTypeId, storeId).then(res => {
                        if (res) {
                            const values = { ...props.values, incentivePartnerId: res.id.toString() };
                            props.setValues(values);
                            setIncentiveState({ isValid: true, error: '' });
                        } else {
                            setIncentiveState({ isValid: false, error: 'Le code ne correspond à aucune offre' });
                        }
                    });
                } else {
                    setIncentiveState({
                        isValid: false,
                        error: buildCodeErrorStr(code, eventTypeId, storeId),
                    });
                }
            }
        },
        [loadingIncentives, onIncentiveExist],
    );

    const convertedEventTypes: SelectControlOption[] = useMemo(
        () =>
            eventTypes
                ? eventTypes.map((eventType, index) => {
                      return {
                          key: `${index}`,
                          value: `${eventType.id}`,
                          text: eventType.name.fr,
                      };
                  })
                : [],
        [eventTypes],
    );

    const convertedStores: SelectControlOption[] = useMemo(
        () =>
            stores
                ? stores.map((store, index) => {
                      return {
                          key: `${index}`,
                          value: `${store.id}`,
                          text: store.name,
                      };
                  })
                : [],
        [stores],
    );

    let initModel = model;
    if (offer) {
        initModel = {
            ...initModel,
            offerId: offer.id.toString(),
        };
    }

    const convertedOffers: SelectControlOption[] = useMemo(
        () =>
            offer && offer.choices
                ? offer.choices.map((choice, index) => {
                      return {
                          key: `${index}`,
                          value: `${choice.id}`,
                          text: choice.name,
                      };
                  })
                : [],
        [offer],
    );

    if (eventTypes && eventTypes.length === 1) {
        initModel.eventTypeId = `${eventTypes[0].id}`;
        initModel.denominatorType = hasOrganiser ? DenominatorTypeValues.RECIPIENT : // si organisateur
        // si pas organisateur mais que le type de base est organisateur
        !hasOrganiser && eventTypes[0].denominatorType === DenominatorTypeValues.RECIPIENT ? DenominatorTypeValues.UNKNOWN : 
        // sinon
        eventTypes[0].denominatorType;
    }

    const isTypeDropdown = convertedEventTypes.length > 3;

    const [hasReferrer, setHasReferrer] = useState(false);
    const [hasCode, setHasCode] = useState(false);

    return (
        <Formik<ListIdentification>
            initialValues={initModel}
            onSubmit={onSuccess}
            render={props => (
                <div className={styles.containerLayout}>
                    {picture && (
                        <div className={styles.bannerContainer}>
                            <CMSImageContainer picture={picture} style={{ width: '100%' }} />
                        </div>
                    )}
                    <div className={styles.formContainer}>
                        <form className={styles.form} onSubmit={props.handleSubmit}>
                            {offer && (
                                <div className={styles.offerContainer}>
                                    <div>{offer.name}</div>
                                    {offer.choices && offer.choices.length > 0 && (
                                        <RadioInputControl
                                            name="offerChoiceId"
                                            required
                                            horizontal
                                            centered
                                            options={convertedOffers}
                                        />
                                    )}
                                </div>
                            )}
                            <div className={styles.formTopSection}>
                                <div className={`${styles.name}`}>
                                    <InputControl name="name" required placeholder="Nom de ma liste*" />
                                </div>
                                {convertedEventTypes && convertedEventTypes.length > 1 && (
                                    <div className={`${styles.type}`}>
                                        {!isTypeDropdown ? (
                                            <RadioInputControl
                                                name="eventTypeId"
                                                required
                                                horizontal
                                                centered
                                                options={convertedEventTypes}
                                                onChange={newVal => {
                                                    const eventType = eventTypes.find(e => e.id === Number(newVal));
                                                    props.setFieldValue(
                                                        'denominatorType',
                                                        // si organisateur
                                                        hasOrganiser ? DenominatorTypeValues.RECIPIENT :
                                                        // si pas organisateur mais que le type de base est organisateur
                                                        !hasOrganiser && eventType && eventType.denominatorType === DenominatorTypeValues.RECIPIENT ? DenominatorTypeValues.UNKNOWN :
                                                        eventType ? eventType.denominatorType : null,
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <SelectControl
                                                name="eventTypeId"
                                                options={convertedEventTypes}
                                                required
                                                placeholder="Type d'événement"
                                                onChange={newVal => {
                                                    const eventType = eventTypes.find(e => e.id === Number(newVal));
                                                    props.setFieldValue(
                                                        'denominatorType',
                                                        // si organisateur
                                                        hasOrganiser ? DenominatorTypeValues.RECIPIENT :
                                                        // si pas organisateur mais que le type de base est organisateur
                                                        !hasOrganiser && eventType && eventType.denominatorType === DenominatorTypeValues.RECIPIENT ? DenominatorTypeValues.UNKNOWN :
                                                        eventType ? eventType.denominatorType : null,
                                                    );
                                                }}
                                            />
                                        )}
                                        <input
                                            type="hidden"
                                            name="denominatorType"
                                            value={props.values.denominatorType}
                                        />
                                    </div>
                                )}
                                <div className={`${styles.formLine} ${styles.date}`}>
                                    <div className={styles.dateLabel}>Évènement prévu le* :</div>
                                    <div className={styles.datePicker}>
                                        <DatePickerControl name="date" required />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.formLine}>
                                <div className={styles.signLabel}>
                                    Je choisis le magasin Printemps qui m&apos;accompagnera dans la gestion de ma liste
                                </div>
                                <div className={styles.signSelect}>
                                    <SelectControl
                                        name="storeId"
                                        options={convertedStores}
                                        required
                                        placeholder="Enseigne*"
                                        style={{ minWidth: '300px' }}
                                    />
                                </div>
                            </div>
                            <div className={`${styles.formLine} ${styles.codes}`}>
                                <div className={styles.formField}>
                                    <button
                                        type="button"
                                        className={styles.inputShowBtn}
                                        onClick={() => setHasReferrer(!hasReferrer)}
                                    >
                                        Je suis parrainé
                                    </button>
                                    {hasReferrer && (
                                        <div className={styles.fullWidth}>
                                            <EmailInputControl
                                                name="referrer"
                                                placeholder="L'adresse mail de mon parrain"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className={styles.formField}>
                                    <button
                                        type="button"
                                        className={styles.inputShowBtn}
                                        onClick={() => setHasCode(!hasCode)}
                                    >
                                        Je dispose d&apos;un code avantage
                                    </button>
                                    {hasCode && (
                                        <div className={styles.fullWidth}>
                                            <InputControl name="code" placeholder="Mon code avantage" />
                                            <div className={styles.codeVerification}>
                                                <button
                                                    className={styles.codeVerificationBtn}
                                                    type="button"
                                                    onClick={() => {
                                                        validateIncentive(props);
                                                    }}
                                                >
                                                    Vérifier la validité du code
                                                </button>
                                            </div>

                                            {!incentiveState.isValid && incentiveState.error && (
                                                <div className={styles.codeError}>{incentiveState.error}</div>
                                            )}
                                            {incentiveState.isValid &&
                                                props.values.eventTypeId &&
                                                props.values.storeId &&
                                                props.values.code &&
                                                !incentiveState.error && (
                                                    <div className={styles.codeValid}>Code Valide</div>
                                                )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className={styles.passwordHintContainer}>
                                <span className={styles.passwordLabel}>Je crée mon mot de passe de connexion</span>
                                <span className={styles.passwordHint}>
                                    Composé d&apos;au moins 8 caractères dont 1 majuscule, 1 minuscule, 1 chiffre et un
                                    des signes suivants : @ # $ % ^ & + = ! ? / \ - _ ~ * , ; :
                                </span>
                            </div>
                            <div className={`${styles.formLine} ${styles.password}`}>
                                <div className={`${styles.formField} ${styles.passwordField}`}>
                                    <div className={styles.fullWidth}>
                                        <PasswordInputControl name="password" required placeholder="Mot de passe*" />
                                    </div>
                                </div>
                                <div className={`${styles.formField} ${styles.passwordConfirmField}`}>
                                    <span className={styles.label}>Confirmation du mot de passe</span>
                                    <div className={styles.fullWidth}>
                                        <PasswordInputControl
                                            name="passwordConfirm"
                                            required
                                            placeholder="Confirmation du mot de passe*"
                                            equalValue={props.values.password}
                                        />
                                    </div>
                                </div>
                            </div>
                            <button className={styles.tabBtn} type="submit">
                                SUIVANT
                            </button>
                        </form>
                    </div>
                </div>
            )}
        />
    );
};

export const IdentificationForm = enhancer(InnerIdentificationForm);
